import { useEffect, useMemo } from "react";
import { SocialPromptModal } from "../Common/Modals/SocialPromptModal";
import {
  ContactEnrichmentStatus,
  INSIGHT_CATEGORY,
  INSIGHT_STATUS,
  type IContactIntelligence,
} from "../../Types/account";
import {
  TUTORIAL_DEMO_CONTACTS,
  TUTORIAL_DEMO_INSIGHTS,
  TUTORIAL_SCENARIO,
} from "../../Constants/tutorialScenario";
import "../../Css/Outreach/OutreachWorkspace.css";
import "../../Css/Outreach/OutreachWorkspace.glass.css";

export type TutorialDemoSocialPromptView = "compose" | "request";

type TutorialDemoSocialPromptModalProps = {
  view?: TutorialDemoSocialPromptView;
  onClose?: () => void;
};

const DEMO_INSIGHT = TUTORIAL_DEMO_INSIGHTS[0];
const DEMO_ACCOUNT_ID = TUTORIAL_SCENARIO.primaryAccount.uuid;

const DEMO_CONTACTS: IContactIntelligence[] = TUTORIAL_DEMO_CONTACTS.map((contact) => {
  const slug = contact.name.toLowerCase().replace(/[^a-z]+/g, "-");
  return {
    id: contact.id,
    name: contact.name,
    title: contact.title,
    email: contact.email,
    email_status: "valid_email",
    linkedin_url: `https://www.linkedin.com/in/${slug}`,
    skills: null,
    summary: null,
    my_commonalities: null,
    relevance_score: 80,
    overall_score: 80,
    direct_phone_number: null,
    mobile_phone_number: "+15555550100",
    corporate_phone_number: null,
    profile_picture_url: null,
    company_name: TUTORIAL_SCENARIO.primaryAccount.name,
    city: null,
    state: null,
    country: null,
    insight_interaction_status: {
      favorite: false,
      index: null,
      notes: null,
      insight_status: INSIGHT_STATUS.NOT_SEEN,
    },
    account: {
      id: 0,
      domain: TUTORIAL_SCENARIO.primaryAccount.domain,
      name: TUTORIAL_SCENARIO.primaryAccount.name,
      client_id: "",
      description: null,
      logo_image_url: null,
      creation_time: new Date(0),
      last_update: new Date(0),
      latest_insight_published_date: null,
      industry: TUTORIAL_SCENARIO.primaryAccount.industry,
      primary_user: null,
      secondary_users: [],
      num_account_insights: 0,
      num_contacts: TUTORIAL_DEMO_CONTACTS.length,
      num_actions: 0,
      managed_by: null,
    },
    enrichment_status: ContactEnrichmentStatus.ENRICHED_SUCCESS,
    decision_making_unit: null,
  } satisfies IContactIntelligence;
});

const DEMO_INSIGHT_CONTEXT = {
  title: DEMO_INSIGHT.title,
  synopsis: DEMO_INSIGHT.summary,
  notes: [] as string[],
  sourceUrl: null as string | null,
};

/** Real outreach workspace shell fed by tour fixtures (same UI as production). */
export function TutorialDemoSocialPromptModal({
  view = "compose",
  onClose,
}: TutorialDemoSocialPromptModalProps) {
  const handleClose = onClose ?? (() => {});
  const forcePage = useMemo(
    () => (view === "request" ? ("request" as const) : ("prompts" as const)),
    [view],
  );

  useEffect(() => {
    const body = document.querySelector<HTMLElement>(".mainLayout_body");
    body?.classList.add("mainLayout_body--outreachOpen");
    return () => {
      body?.classList.remove("mainLayout_body--outreachOpen");
    };
  }, []);

  return (
    <div className="tutorialDemoOverlay_backdrop tutorialDemoOverlay_backdrop--outreach">
      <div
        className="outreachWorkspace_overlay tutorialDemoOverlay_outreachOverlay"
        data-tour="social-prompt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={
          view === "request"
            ? "Sample request new social prompts"
            : "Sample social prompt workspace"
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="outreachWorkspace_page">
          <SocialPromptModal
            presentation="workspace"
            socialPromptId={null}
            insightId={DEMO_INSIGHT.id}
            category={INSIGHT_CATEGORY.ACCOUNT}
            onUpdateCompletedSocialPrompts={() => {}}
            onClose={handleClose}
            accountId={DEMO_ACCOUNT_ID}
            possibleContacts={DEMO_CONTACTS}
            insightContext={DEMO_INSIGHT_CONTEXT}
            forcePage={forcePage}
            initialStepNumber={1}
          />
        </div>
      </div>
    </div>
  );
}
